import {DimensionFingerprint, ElevationIdentity} from "./elevationTool/shared";
import {DrwRenderingMeta, Opening} from "./Openings";
import {QuoteOpening} from "./QuoteOpenings";
import {sleep} from "nate-react-api-helpers";
import {SubAPI} from "./SubAPI";
import {DoorElevation, HingeGroupSplit, SavePreflight, SavePreflightInput, ThreePartElevation} from "./elevationTool/ThreePartElevation";
import {DoorElevationData} from "./elevationTool/DoorElevationData";
import {SerGlass} from "./elevationTool/shared";

export type ElevationRenderKind = "screen" | "door" | "hardware" | "cutplan";

// one opening property an elevation adopt would overwrite (mirrors drw.IdentityChange),
// for the confirm-before-adopt prompt.
export type AdoptIdentityChange = {field: string; from: string; to: string};

// what the headless render page draws for one content-addressed drw_rendering row: a representative
// opening to load and the single kind. Returned by GET /api/drw/render-job. When quote is true the
// opening is a quote_opening id and alternative is its quote_alternative (the render page lists the
// quote opening by id within it); in production quote is false and alternative is 0.
export type RenderJob = {
    project: number;
    opening: number;
    quote: boolean;
    alternative: number;
    kinds: ElevationRenderKind[];
    label: string;
};

// one not-ready rendering for a project, with a representative opening for display.
export type RenderingStatusItem = {
    rendering: number;
    kind: ElevationRenderKind;
    status: "pending" | "failed";
    label: string;
    opening: number;
    openingName: string;
};

// the project's outstanding renderings, for the submittal "render now & wait" gate.
export type RenderingStatus = {
    pending: number;
    failed: number;
    items: RenderingStatusItem[];
};

// one lite an export measured, as the headless render page reports it back to the render worker.
// The cut-out comes out of the frame-grid layout solver, so it exists only here and in
// drw_rendering.glass_json — it can't be read back off the elevation. Mirrors drw.SerGlazingPanel;
// the sizes are in inches, like SerFrmVar.in.
export type RenderGlassPanel = {
    mark: string;
    glazing: string;
    cutOutWidthIn: number;
    cutOutHeightIn: number;
    // the short end of a trapezoid lite (under a sloped head); omitted for a rectangle.
    cutOutHeightShortIn?: number;
    glass: SerGlass;
};

// one anchor section of a frame, as the headless render reports it (drw_rendering.anchors_json).
// An auto qty comes out of the frame-grid layout solver, so the resolved qty exists only here.
export type RenderAnchorSection = {
    stickId: string;
    side: "top" | "bottom" | "left" | "right";
    product: number;
    qty: number;
    lengthIn: number;
};

// the products an anchor section can use (product categories named like "%anchor"), and the
// company default (null when none is set).
export type AnchorProducts = {
    products: {id: number; name: string; productCode: string}[];
    defaultProduct: number | null;
};

// one frame stick on the cut plan, as the headless render reports it (drw_rendering.cut_plan_json).
// The lengths come out of the frame-grid layout solver, so they exist only here. Sizes are in inches.
// A vertical stick's faces are left/right and its ends top/bottom; a horizontal stick's faces are
// top/bottom and its ends left/right. Ends are listed A (top/left) first.
export type CutPlanStick = {
    label: string;
    orientation: "vertical" | "horizontal";
    role: "jamb" | "head" | "sill" | "threshold" | "bottomRun" | "mullion";
    sided: "one" | "two";
    faces: CutPlanFace[];
    // the stick's extent along its axis, measured along the slope on a sloped stick.
    nominalIn: number;
    // = ends[0].addIn + nominalIn + ends[1].addIn - undercutIn
    lengthIn: number;
    thicknessIn: number;
    undercutIn: number;
    // the slope off horizontal/vertical in degrees; omitted for a straight stick.
    slopeDeg?: number;
    ends: CutPlanEnd[];
    // the sticks that cap into this one along its span; positionIn is from the A end of the cut
    // piece to the capping stick's centre line.
    notches: {label: string; positionIn: number}[];
    // an angled mullion: its plan angle (measured on the location-one side) and each half's
    // face sizes. The inside face keeps the jamb face; the outside face runs on to meet the
    // other half's in a point. Omitted for a straight stick.
    angled?: {deg: number; insideFaceIn: number; outsideFaceIn: number};
    // a hinge jamb: each hinge, top down, from the A end of the cut piece (past the end
    // allowance, like a notch) to the top of the hinge, with the hinge's height.
    hinges?: {topIn: number; heightIn: number}[];
    // a strike jamb (single door): the lock strike and, when the door has a deadbolt, the
    // deadbolt strike, each from the A end of the cut piece to its centre.
    strikes?: {kind: "lock" | "deadbolt"; centerIn: number}[];
};

export type CutPlanFace = {
    side: "left" | "right" | "top" | "bottom";
    profiles: string[];
    interfaces: {kind: "lite" | "fixedPanel" | "doors" | "wall"; mark?: string}[];
};

// "square" is a free end that meets no stick (e.g. a mullion that runs to the floor).
export type CutPlanEnd = {
    side: "left" | "right" | "top" | "bottom";
    contour: "cap" | "miter" | "extend" | "square";
    otherLabel?: string;
    // how far the cut piece runs past the nominal end along the stick (to the long
    // point of an angled cut).
    addIn: number;
    // the end cut's angle off square in degrees: 0 for a square cut, 45 for a miter
    // between two straight sticks, the slope's angle where a stick meets a sloped one.
    angleDeg: number;
};

// one opening built to a cut-plan variant. Mirrors drw.CutPlanOpeningRef.
export type CutPlanOpeningRef = {
    id: number;
    name: string;
    qty: number;
    nominalWidth: string;
    nominalHeight: string;
};

// one screen elevation variant on the frame cut plan. Mirrors drw.CutPlanGroup.
export type CutPlanGroup = {
    elevation: number;
    name: string;
    label: string;
    rendering: number;
    cutPlan: {sticks: CutPlanStick[]} | null;
    openings: CutPlanOpeningRef[];
};

// one project's share of a purchase order's frame cut plan. Mirrors drw.CutPlanProjectGroup.
export type CutPlanProjectGroup = {
    project: number;
    groups: CutPlanGroup[];
};

// one glass panel to order, formatted for the glazing submittal. Mirrors drw.GlazingPanel.
export type GlazingPanel = {
    mark: string;
    alignment: string;
    thickness: string;
    kind: string;
    bead: string;
    // the same bead in its stored form ("thermal" | "screwIn" | "snapIn"), for picking the bead
    // profile drawing (see BeadProfiles).
    beadKey: string;
    thermal: string;
    nominalSize: string;
    actualSize: string;
};

// one opening that takes an elevation's glass. Mirrors drw.GlazingOpeningRef.
export type GlazingOpeningRef = {
    id: number;
    name: string;
    qty: number;
};

// one screen (frame) elevation variant on the glazing submittal: the drawing (a rendering file id),
// the panels its grid needs, and the openings built to it. Mirrors drw.GlazingScreenGroup.
export type GlazingScreenGroup = {
    project: number;
    projectName: string;
    elevation: number;
    name: string;
    label: string;
    rendering: number;
    panels: GlazingPanel[];
    openings: GlazingOpeningRef[];
};

// one door elevation variant on the glazing submittal. Mirrors drw.GlazingDoorGroup.
export type GlazingDoorGroup = {
    project: number;
    projectName: string;
    elevation: number;
    name: string;
    label: string;
    rendering: number;
    lites: DoorLiteDetail[];
    openings: GlazingOpeningRef[];
};

// everything the glazing submittal PDF draws. Mirrors drw.GlazingSubmittal.
// one-off glass: glass no elevation drew, so it has no drawing and no section of its own. Carries
// what the glass summary lists.
export type GlazingCustomItem = {
    size: string;
    type: string;
    thickness: string;
    qty: number;
};

export type GlazingSubmittal = {
    screens: GlazingScreenGroup[];
    doors: GlazingDoorGroup[];
    custom: GlazingCustomItem[];
};

// one lite of a door elevation, formatted for display. Marks run G1..Gn in drawing order, and
// the sizes are already in the project's unit system. Mirrors drw.DoorLiteDetail.
export type DoorLiteDetail = {
    mark: string;
    cutOut: string;
    glassSize: string;
    type: string;
    thermal: string;
    thickness: string;
    bead: string;
    beadKey: string;
};

// one door elevation's lites. Mirrors drw.DoorElevationLites.
export type DoorElevationLites = {
    elevation: number;
    name: string;
    lites: DoorLiteDetail[];
};

// one opening that links a drw elevation, for the review screens.
export type ReviewOpeningRef = {
    id: number;
    name: string;
    // the opening's screen rendering measurements (screen review only; null until rendered)
    renderingMeta?: DrwRenderingMeta | null;
};

// a drw elevation plus the openings that link it. The review screens open the editor at the
// first opening; an empty openings list means the elevation is orphaned and can't be set up
// until an opening links it.
export type ElevationReviewItem = {
    id: number;
    name: string;
    openings: ReviewOpeningRef[];
    // a global door elevation, editable only from /projects/settings.
    global?: boolean;
};

// one option of the door elevation picker. Mirrors drw.DoorElevationOption.
export type DoorElevationOption = {
    id: number;
    name: string;
    // a global door elevation from /projects/settings.
    global: boolean;
    // a file-based (v1) door elevation name. id is 0.
    legacy: boolean;
};

// a global door elevation name with its drawing (null until drawn). Mirrors drw.GlobalDoorElevation.
export type GlobalDoorElevation = {
    ddl: number;
    name: string;
    door: DoorElevation | null;
};

// an opening or quote opening that links a global door elevation. Mirrors drw.GlobalDoorElevationLink.
export type GlobalDoorElevationLink = {
    project: number;
    projectName: string;
    opening: number | null;
    quoteOpening: number | null;
    alternative: number | null;
    name: string;
    // true when a re-render reaches this opening.
    rerendered: boolean;
};

// endpoints backing the 2D elevation tool (core/opening/drw). The door/screen/frame
// and hardware elevations that make up an opening's drawing. Types live in
// ./elevationTool.
export type ScreenSlotOption = {
    primaryOpening: number;
    primaryOpeningName: string;
    screenName: string;
    slot: string;
};

export type SlotOpening = {
    id: number;
    name: string;
    // the slot it fills on this frame, if any.
    slot: string | null;
    // filling a slot unlinks its own screen elevation.
    hasScreen: boolean;
    // size its door cell; stored on the slot when the screen is saved.
    identity: ElevationIdentity;
    dimensions: DimensionFingerprint;
};

export class ElevationTool extends SubAPI {

    // the render instructions for a drw_rendering row, for the headless render page. Authenticated by
    // the short-lived render bearer token the page sets on the fetcher before calling.
    renderJob(props: {rendering: number}) {
        return this.fetcher.get<RenderJob>("/api/drw/render-job", props);
    }

    anchorProducts() {
        return this.fetcher.get<AnchorProducts>("/api/drw/anchor-products", {});
    }

    // the project's not-yet-ready renderings (pending/failed counts + items). Poll to know when a
    // submittal's images are ready.
    renderingStatus(props: {project: number}) {
        return this.fetcher.get<RenderingStatus>("/api/drw/rendering-status", props);
    }

    // bump the project's pending renderings to the front of the render queue.
    prioritizeRenderings(props: {project: number}) {
        return this.fetcher.post<{prioritized: number}>("/api/drw/rendering-prioritize", props);
    }

    // re-render (in place) the project's renderings produced by an older render version, so a submittal
    // embeds current-code images. Call before checking renderingStatus so the reset rows show as pending.
    refreshRenderings(props: {project: number}) {
        return this.fetcher.post<{refreshed: number}>("/api/drw/rendering-refresh", props);
    }

    // resolve the door/screen/hardware elevation triple by name within a scope. The
    // *Elev params are elevation names; omit one to leave that slot null.
    getThreePart(props: {
        project: number;
        alternative?: number;
        doorElev?: string;
        screenElev?: string;
        hwElev?: string;
    }) {
        return this.fetcher.get<ThreePartElevation>("/api/drw/three-part-elevation", props);
    }

    // resolve an opening's three elevations by following its drw_* FK links (by id),
    // for loading the tool. Any of the three may be null (that spec still uses the
    // legacy file).
    getForOpening(props: {project: number; opening: number}) {
        return this.fetcher.get<ThreePartElevation>("/api/drw/three-part-elevation/for-opening", props);
    }

    // quote-mode equivalent of getForOpening: resolve a quote opening's three elevations by
    // following its drw_* FK links (by id), for loading the tool. Any of the three may be null.
    getForQuoteOpening(props: {project: number; quoteOpening: number}) {
        return this.fetcher.get<ThreePartElevation>("/api/drw/three-part-elevation/for-quote-opening", props);
    }

    // what the equivalent save would stop and ask about (name collision, shared-identity
    // fan-out, nominal-dimension write-back), in one read-only call so the tool can prompt for
    // all of them at once. Advisory — the save re-checks.
    savePreflight(props: SavePreflightInput) {
        return this.fetcher.post<SavePreflight>("/api/drw/three-part-elevation/preflight", props);
    }

    // upsert whichever of the three elevations are present and point the target
    // opening's drw_* links at them. Runs as a background task: queue it, then poll
    // until done and return the task result (the saved triple, or a conflict payload).
    async upsertThreePart(props: ThreePartElevation) {
        const {task} = await this.fetcher.post<{task: string}>("/api/drw/three-part-elevation", props);

        await sleep(500);

        while(true) {
            const status = await this.fetcher.get<{done: boolean; result?: any}>("/api/util/get-background-task", {task, withResult: true});
            if(status.done) {
                if(status.result?.error) throw new Error(status.result.error);
                return status.result as ThreePartElevation;
            }

            await sleep(1000);
        }
    }

    // the screen (frame) elevations available to link, for the openings-grid autocomplete.
    // Pass the target opening (or quoteOpening) to hide screens of an incompatible opening type.
    listScreenElevations(props: {project: number; opening?: number; quoteOpening?: number; alternative?: number; search?: string}) {
        return this.fetcher.get<{id: number; name: string}[]>("/api/drw/screen-elevations", props);
    }

    // point an opening (or quote opening) at an existing screen elevation and adopt that
    // screen's identity (overwrites frame profile etc. to match). When the adopt would
    // change opening properties and no resolution is set, nothing is saved and
    // identityChanges lists them so the caller can prompt, then re-send with confirm
    // (update the opening) or — when canCopy — copy (link a private clone carrying the
    // opening's own identity instead).
    adoptScreen(props: {project: number; opening?: number; quoteOpening?: number; screen: number; confirm?: boolean; copy?: boolean}) {
        return this.fetcher.post<{ok: boolean; identityChanges?: AdoptIdentityChange[]; canCopy?: boolean}>("/api/drw/screen-elevation/adopt", props);
    }

    // the free door slots on other openings' frames that this opening (or quote opening) can fill.
    listScreenSlots(props: {project: number; opening?: number; quoteOpening?: number}) {
        return this.fetcher.get<ScreenSlotOption[]>("/api/drw/screen-slots", props);
    }

    // for the primary opening the tool edits: the openings on its frame, and the openings that
    // could fill one of its door slots.
    listSlotOpenings(props: {project: number; opening?: number; quoteOpening?: number}) {
        return this.fetcher.get<{linked: SlotOpening[]; candidates: SlotOpening[]}>("/api/drw/screen-slot-openings", props);
    }

    // put an opening (or quote opening) into a door slot on another opening's frame. An opening
    // with its own screen elevation is unlinked from it in the same write.
    adoptScreenSlot(props: {project: number; opening?: number; quoteOpening?: number; primaryOpening: number; slot: string}) {
        return this.fetcher.post<{ok: boolean}>("/api/drw/screen-slot/adopt", props);
    }

    // clear an opening's screen (frame) elevation link so it falls back to the legacy
    // fields (identity columns kept). Used before opening the tool to draw a fresh one.
    // legacyOpening / legacyQuoteOpening save the edited legacy fields in the same request.
    unlinkScreen(props: {project: number; opening?: number; quoteOpening?: number; legacyOpening?: Opening; legacyQuoteOpening?: QuoteOpening}) {
        return this.fetcher.post<{ok: boolean}>("/api/drw/screen-elevation/unlink", props);
    }

    // the door elevations available to link, for the openings-grid autocomplete.
    // Pass the target opening (or quoteOpening) to hide doors of an incompatible opening type.
    listDoorElevations(props: {project: number; opening?: number; quoteOpening?: number; alternative?: number; search?: string}) {
        return this.fetcher.get<DoorElevationOption[]>("/api/drw/door-elevations", props);
    }

    // a global door elevation (by its drwDoorElevation ddl row), for the tool's edit-global mode.
    getGlobalDoor(props: {ddl: number}) {
        return this.fetcher.get<GlobalDoorElevation>("/api/drw/global-door-elevation", props);
    }

    // the openings that link a global door elevation.
    globalDoorLinks(props: {ddl: number}) {
        return this.fetcher.get<GlobalDoorElevationLink[]>("/api/drw/global-door-elevation/links", props);
    }

    // save the drawing of a global door elevation. rerender also updates the linked openings.
    // Runs as a background task, like upsertThreePart.
    async upsertGlobalDoor(props: {ddl: number; dataJson: DoorElevationData; rerender: boolean}) {
        const {task} = await this.fetcher.post<{task: string}>("/api/drw/global-door-elevation", props);

        await sleep(500);

        while(true) {
            const status = await this.fetcher.get<{done: boolean; result?: any}>("/api/util/get-background-task", {task, withResult: true});
            if(status.done) {
                if(status.result?.error) throw new Error(status.result.error);
                return status.result as GlobalDoorElevation;
            }

            await sleep(1000);
        }
    }

    // point an opening (or quote opening) at an existing door elevation. Unlike adoptScreen
    // this doesn't touch identity (frame profile etc. are frame-anchored).
    adoptDoor(props: {project: number; opening?: number; quoteOpening?: number; door: number}) {
        return this.fetcher.post<{ok: boolean}>("/api/drw/door-elevation/adopt", props);
    }

    // clear an opening's door elevation link. Used before opening the tool to draw a fresh one.
    // legacyOpening / legacyQuoteOpening save the edited legacy fields in the same request.
    unlinkDoor(props: {project: number; opening?: number; quoteOpening?: number; legacyOpening?: Opening; legacyQuoteOpening?: QuoteOpening}) {
        return this.fetcher.post<{ok: boolean}>("/api/drw/door-elevation/unlink", props);
    }

    // the hardware elevations available to link, for the openings-grid autocomplete.
    listHardwareElevations(props: {project: number; alternative?: number; search?: string}) {
        return this.fetcher.get<{id: number; name: string}[]>("/api/drw/hardware-elevations", props);
    }

    // point an opening (or quote opening) at an existing hardware elevation. Like adoptDoor
    // this doesn't touch identity (frame profile etc. are frame-anchored).
    // When the elevation's hinge layout needs the opening moved out of its hardware group, nothing is
    // linked and hingeGroupSplit says so; re-send with confirm to link and move it.
    adoptHardware(props: {project: number; opening?: number; quoteOpening?: number; hardware: number; confirm?: boolean}) {
        return this.fetcher.post<{ok?: boolean; hingeGroupSplit?: HingeGroupSplit | null}>("/api/drw/hardware-elevation/adopt", props);
    }

    // clear an opening's hardware elevation link. Used before opening the tool to draw a fresh one.
    unlinkHardware(props: {project: number; opening?: number; quoteOpening?: number}) {
        return this.fetcher.post<{ok: boolean}>("/api/drw/hardware-elevation/unlink", props);
    }

    // the lite details of several door elevations in one call, for the submittal's debug page.
    // An id that isn't in the project comes back missing rather than as an error.
    doorElevationLites(props: {project: number; ids: number[]}) {
        return this.fetcher.post<DoorElevationLites[]>("/api/drw/door-elevations/lites", props);
    }

    // the glass panels the given openings need, grouped by the elevation variant they were drawn on.
    // A screen's panels come from its rendering, so an opening whose variant hasn't rendered yet is
    // left out — gate on renderingStatus first (see SubmittalRenderGate).
    glazingSubmittal(props: {project: number; alternative?: number; openings: number[]}) {
        return this.fetcher.post<GlazingSubmittal>("/api/drw/glazing-submittal", props);
    }

    // the frame cut plan of each screen elevation variant the given openings are built to. A variant
    // that hasn't rendered yet is left out — gate on renderingStatus first (see SubmittalRenderGate).
    frameCutPlans(props: {project: number; alternative?: number; openings: number[]}) {
        return this.fetcher.post<CutPlanGroup[]>("/api/drw/frame-cut-plans", props);
    }

    // the frame cut plans of the openings whose frames are on a purchase order, per project.
    frameCutPlansForPurchaseOrder(props: {id: number}) {
        return this.fetcher.get<CutPlanProjectGroup[]>("/api/drw/frame-cut-plans/for-purchase-order", props);
    }

    // the glass on a purchase order, in the submittal's shape, for the PO PDF's glazing attachment.
    glazingForPurchaseOrder(props: {id: number}) {
        return this.fetcher.get<GlazingSubmittal>("/api/drw/glazing-submittal/for-purchase-order", props);
    }

    // every screen (frame) elevation in scope with the openings that link it, for the review screen.
    reviewScreenElevations(props: {project: number; alternative?: number}) {
        return this.fetcher.get<ElevationReviewItem[]>("/api/drw/screen-elevations/review", props);
    }

    // every door elevation in scope with the openings that link it, for the review screen.
    reviewDoorElevations(props: {project: number; alternative?: number}) {
        return this.fetcher.get<ElevationReviewItem[]>("/api/drw/door-elevations/review", props);
    }

    // every hardware elevation in scope with the openings that link it, for the review screen.
    reviewHardwareElevations(props: {project: number; alternative?: number}) {
        return this.fetcher.get<ElevationReviewItem[]>("/api/drw/hardware-elevations/review", props);
    }
}
